import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, PieChart, RefreshCw } from "lucide-react";
import { API, authHeaders } from "./shared";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CAT_META = [
  ["base_rental", "Base Rental Revenue", "text-[#F2B705]"],
  ["coverage", "All-In Coverage Revenue", "text-sky-300"],
  ["damage_waiver", "Damage Waiver Revenue", "text-emerald-300"],
  ["fees", "Additional Fee Revenue", "text-purple-300"],
  ["extension", "Extension Revenue", "text-orange-300"],
  ["late_fee", "Late Fee Revenue", "text-red-300"],
  ["other", "Other Charges", "text-white/70"],
  ["tax", "Taxes Collected", "text-white/70"],
  ["legacy_unitemized", "Legacy (pre-engine bookings)", "text-white/40"],
];

export default function RevenueMixModule({ token }) {
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/pricing/revenue`, authHeaders(token)).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading revenue mix…</div>;
  if (d.error) return <p className="text-white/40 text-sm py-10 text-center">Could not load revenue analytics.</p>;

  const cats = d.categories || {};
  const max = Math.max(...CAT_META.map(([k]) => cats[k] || 0), 1);

  return (
    <div data-testid="revenue-mix" className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><PieChart size={17} className="text-[#F2B705]" /> Revenue by Category</h3>
          <p className="text-white/45 text-sm mt-0.5">Exactly where every dollar of billed revenue comes from — {d.itemized_count} itemized booking(s), {d.legacy_count} from before the pricing engine.</p>
        </div>
        <button data-testid="revenue-mix-refresh" onClick={load} className="text-white/40 hover:text-white text-xs flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["AutoHire Revenue (excl. insurance)", money(d.total_billed_excl_insurance), "text-emerald-300", "revenue-mix-autohire-revenue"],
          ["All-In Coverage Billed (third-party, not AutoHire revenue)", money(d.total_insurance_billed), "text-sky-300", "revenue-mix-insurance-billed"],
          ["Unallocated (legacy All-In, no reliable breakdown)", money(d.total_unallocated_legacy), "text-amber-300", "revenue-mix-unallocated"],
          ["Total Billed (all categories incl. insurance)", money(d.total_billed), "text-white", "revenue-mix-total-billed"],
          ["Collected (cash received, all-time)", money(d.total_collected), "text-white", "revenue-mix-total-collected"],
          ["Discounts Given", `-${money(d.discounts_total)}`, "text-white", "revenue-mix-discounts"],
          ["Bookings", d.bookings_count, "text-white", "revenue-mix-bookings-count"]].map(([l, v, tone, testid]) => (
          <div key={l} data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
            <div className="text-white/45 text-xs uppercase tracking-widest">{l}</div>
            <div className={`font-display font-black text-xl mt-1 ${tone}`}>{v}</div>
          </div>
        ))}
      </div>
      <p className="text-white/35 text-[11px] -mt-2">
        All-In third-party coverage is collected from renters but is never AutoHire rental earnings — it's excluded from
        the AutoHire Revenue figure above. All-In bookings with no reliable rate of their own (pre-pricing-engine legacy)
        are never guessed using today's rate; they're reported as Unallocated instead.
      </p>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5 space-y-2.5" data-testid="revenue-category-bars">
        {CAT_META.map(([k, label, color]) => (
          <div key={k}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{label}</span>
              <span className={`font-semibold ${color}`} data-testid={`revenue-cat-${k}`}>{money(cats[k])}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full mt-1">
              <div className="h-full rounded-full bg-[#F2B705]/70" style={{ width: `${Math.min(100, ((cats[k] || 0) / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {d.by_key && (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="revenue-by-key">
          <h4 className="text-white font-semibold text-sm mb-3">Package &amp; Fee Detail
            {d.package_counts && <span className="text-white/35 text-xs font-normal ml-2">{d.package_counts.three_day_package} × 3-Day Special · {d.package_counts.weekly_special} × Weekly Special</span>}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[["three_day_package", "3-Day Package"], ["weekly_special", "Weekly Special"], ["additional_days", "Additional Days"],
              ["young_driver", "Young Driver"], ["additional_driver", "Additional Driver"], ["delivery", "Delivery"],
              ["airport", "Airport"], ["cleaning", "Cleaning"], ["fuel", "Fuel"], ["convenience", "Convenience"]].map(([k, l]) => (
              <div key={k}>
                <div className="text-white/40 text-xs">{l}</div>
                <div className="text-white font-semibold text-sm" data-testid={`revenue-key-${k}`}>{money(d.by_key[k])}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {[["Avg Revenue / Booking", money(d.avg_per_booking)], ["Avg Revenue / Rental Day", money(d.avg_per_day)], ["Avg Revenue / Vehicle", money(d.avg_per_vehicle)]].map(([l, v]) => (
          <div key={l} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4 text-center">
            <div className="font-display font-black text-lg text-[#F2B705]">{v}</div>
            <div className="text-white/40 text-xs mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
        <h4 className="text-white font-semibold text-sm mb-3">Revenue per Vehicle</h4>
        <div className="space-y-1.5">
          {(d.per_vehicle || []).map((v) => (
            <div key={v.vehicle} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5 flex-wrap gap-1">
              <span className="text-white/75">{v.vehicle} <span className="text-white/30 text-xs">({v.bookings} booking{v.bookings !== 1 ? "s" : ""})</span></span>
              <span className="flex items-center gap-3">
                <span className="text-emerald-300 font-semibold" title="AutoHire revenue, excl. insurance">{money(v.revenue_excl_insurance)}</span>
                {v.insurance_billed > 0 && <span className="text-sky-300 text-xs" title="All-In coverage billed (third-party)">+{money(v.insurance_billed)} ins.</span>}
                {v.unallocated > 0 && <span className="text-amber-300 text-xs" title="Unallocated legacy">+{money(v.unallocated)} unalloc.</span>}
              </span>
            </div>
          ))}
          {(!d.per_vehicle || d.per_vehicle.length === 0) && <p className="text-white/40 text-sm">No bookings yet.</p>}
        </div>
      </div>
    </div>
  );
}
